import React, { useEffect, useState, createContext, useContext } from 'react';
import { getCompanies } from '../utils/companyManagement';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
export interface Order {
  id: string;
  orderNo: string;
  orderDate: string;
  orderDateISO?: string; // Add ISO format for filtering
  deliveryDate: string;
  type: string;
  name: string;
  company: string;
  branch: string;
  product: string;
  cashier: string;
  quantity: number;
  catalogType: string;
  subtotal: string;
  discount: string;
  subsidyAmount?: string;
  subsidyAppliedBy?: string;
  promotionId?: string;
  promotionDiscount?: string;
  total: string;
  status: 'Pendiente' | 'En Preparación' | 'Listo' | 'Entregado' | 'Cancelado';
  labelStatus: 'Pendiente' | 'Generado';
  taxExempt?: boolean;
  employeeId?: string;
  claimId?: string;
}
interface OrderContextType {
  orders: Order[];
  loading: boolean;
  // NUEVO: Función expuesta para recargar datos manualmente
  refreshOrders: () => Promise<void>;
  addOrder: (order: Omit<Order, 'id' | 'orderNo' | 'orderDate' | 'status' | 'labelStatus'>) => Promise<void>;
  updateOrderStatus: (orderId: string, status: Order['status']) => Promise<void>;
  updateLabelStatus: (orderIds: string[], status: 'Generado') => Promise<void>;
  cancelOrder: (orderId: string) => Promise<void>;
  applySubsidy: (orderId: string, subsidyAmount: string, appliedBy: string) => Promise<void>;
  filteredOrders: (searchTerm: string, company: string, status?: string) => Order[];
}
const OrderContext = createContext<OrderContextType | undefined>(undefined);
function calculateSubsidyAmount(subtotal: number, subsidyConfig: string): number {
  if (subsidyConfig.includes('%')) {
    const percentage = parseFloat(subsidyConfig.replace('%', ''));
    return subtotal * (percentage / 100);
  } else if (subsidyConfig.includes('RD$')) {
    return parseFloat(subsidyConfig.replace('RD$', '').trim());
  }
  return 0;
}

// Convert Supabase order to app order format
function convertSupabaseOrder(supabaseOrder: any): Order {
  return {
    id: supabaseOrder.id,
    orderNo: supabaseOrder.order_no,
    orderDate: new Date(supabaseOrder.order_date).toLocaleString('es-DO'),
    orderDateISO: supabaseOrder.order_date,
    deliveryDate: supabaseOrder.delivery_date ? new Date(supabaseOrder.delivery_date).toLocaleString('es-DO') : '',
    type: supabaseOrder.type,
    name: supabaseOrder.employee_name,
    company: supabaseOrder.company,
    branch: supabaseOrder.branch,
    product: supabaseOrder.product,
    cashier: 'Online',
    quantity: supabaseOrder.quantity,
    catalogType: supabaseOrder.catalog_type,
    subtotal: String(supabaseOrder.subtotal),
    discount: String(supabaseOrder.discount),
    subsidyAmount: String(supabaseOrder.subsidy_amount || '0'),
    subsidyAppliedBy: supabaseOrder.subsidy_applied_by,
    total: String(supabaseOrder.total),
    status: supabaseOrder.status,
    labelStatus: supabaseOrder.label_status,
    taxExempt: supabaseOrder.tax_exempt,
    employeeId: supabaseOrder.employee_id
  };
}

// Convert app order to Supabase format
function convertToSupabaseOrder(order: any): any {
  return {
    id: order.id,
    order_no: order.orderNo,
    order_date: new Date().toISOString(),
    delivery_date: null,
    type: order.type,
    employee_name: order.name,
    employee_id: order.employeeId || null,
    company: order.company,
    branch: order.branch,
    product: order.product,
    quantity: order.quantity,
    catalog_type: order.catalogType,
    subtotal: parseFloat(order.subtotal),
    discount: parseFloat(order.discount || '0'),
    subsidy_amount: parseFloat(order.subsidyAmount || '0'),
    subsidy_applied_by: order.subsidyAppliedBy || null,
    total: parseFloat(order.total),
    status: order.status,
    label_status: order.labelStatus,
    tax_exempt: order.taxExempt || false
  };
}
export function OrderProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load orders from Supabase ONLY
  // Esta función ahora también servirá como refreshOrders
  const loadOrders = async () => {
    try {
      if (!isSupabaseConfigured()) {
        setError('Supabase no está configurado. Por favor configura las variables de entorno.');
        setLoading(false);
        return;
      }

      // Comentario: Si quieres evitar el parpadeo de carga en cada refresh automático,
      // puedes condicionar este setLoading, pero por seguridad lo dejamos así.
      // console.log('📊 Loading orders from Supabase...') 

      const supabaseOrders = await supabaseClient.getOrders();
      const convertedOrders = supabaseOrders.map(convertSupabaseOrder);
      setOrders(convertedOrders);
      setError(null);
      // console.log(`✅ Loaded ${convertedOrders.length} orders from Supabase`)
    } catch (error: any) {
      console.error('❌ Error loading orders:', error);
      setError(error.message || 'Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  };

  // Load orders on mount
  useEffect(() => {
    loadOrders();
  }, []);

  // Real-time subscription (NO auto-refresh polling)
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    console.log('🔴 Setting up Supabase real-time subscription...');
    try {
      const subscription = supabaseClient.subscribeToOrders(payload => {
        console.log('🔴 Real-time update received:', payload.eventType);
        loadOrders();
      });
      return () => {
        console.log('🔴 Unsubscribing from real-time updates');
        subscription.unsubscribe();
      };
    } catch (error) {
      console.error('❌ Error setting up subscription:', error);
    }
  }, []);

  // Add new order
  const addOrder = async (newOrderData: Omit<Order, 'id' | 'orderNo' | 'orderDate' | 'status' | 'labelStatus'>) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase no está configurado');
    }
    console.log('🔵 Creating new order...');
    const companies = getCompanies();
    const company = companies.find(c => c.name === newOrderData.company);

    // Parse values
    const orderSubtotal = parseFloat(newOrderData.subtotal);
    const isTaxExempt = newOrderData.taxExempt || false;
    let subsidyAmount = '0';
    let subsidyAppliedBy = undefined;
    let finalTotal = 0;

    // Calculate subsidy
    if (company && company.subsidy && company.subsidy !== '0' && company.subsidy !== 'RD$ 0') {
      const calculatedSubsidy = calculateSubsidyAmount(orderSubtotal, company.subsidy);
      if (calculatedSubsidy > 0) {
        subsidyAmount = calculatedSubsidy.toFixed(2);
        subsidyAppliedBy = 'Sistema (Automático)';
      }
    }

    // Calculate final total
    const subtotalAfterSubsidy = orderSubtotal - parseFloat(subsidyAmount);
    if (isTaxExempt) {
      finalTotal = subtotalAfterSubsidy;
    } else {
      finalTotal = subtotalAfterSubsidy * 1.18;
    }
    const newOrder: Order = {
      ...newOrderData,
      id: `order-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      orderNo: Math.floor(100000000 + Math.random() * 900000000).toString(),
      orderDate: new Date().toLocaleString('es-DO'),
      status: 'Pendiente',
      labelStatus: 'Pendiente',
      subsidyAmount,
      subsidyAppliedBy,
      total: finalTotal.toFixed(2)
    };
    console.log('✅ New order created:', newOrder);
    try {
      console.log('📤 Saving order to Supabase...');
      const supabaseOrder = convertToSupabaseOrder(newOrder);
      await supabaseClient.createOrder(supabaseOrder);
      console.log('✅ Order saved to Supabase successfully!');
      await loadOrders();
    } catch (error: any) {
      console.error('❌ Error saving to Supabase:', error);
      throw new Error('Error al guardar el pedido: ' + error.message);
    }
  };

  // Update order status
  const updateOrderStatus = async (orderId: string, status: Order['status']) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase no está configurado');
    }
    console.log(`🔄 Updating order ${orderId} status to ${status}`);
    try {
      await supabaseClient.updateOrder(orderId, {
        status
      });
      console.log('✅ Order status updated in Supabase');
      await loadOrders();
    } catch (error: any) {
      console.error('❌ Error updating order in Supabase:', error);
      throw new Error('Error al actualizar el pedido: ' + error.message);
    }
  };

  // Update label status
  const updateLabelStatus = async (orderIds: string[], status: 'Generado') => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase no está configurado');
    }
    console.log(`🏷️ Updating label status for ${orderIds.length} orders`);
    try {
      for (const orderId of orderIds) {
        await supabaseClient.updateOrder(orderId, {
          label_status: status
        });
      }
      console.log('✅ Label status updated in Supabase');
      await loadOrders();
    } catch (error: any) {
      console.error('❌ Error updating labels in Supabase:', error);
      throw new Error('Error al actualizar etiquetas: ' + error.message);
    }
  };

  // Cancel order
  const cancelOrder = async (orderId: string) => {
    await updateOrderStatus(orderId, 'Cancelado');
  };

  // Apply subsidy
  const applySubsidy = async (orderId: string, subsidyAmount: string, appliedBy: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase no está configurado');
    }
    console.log(`💰 Applying subsidy to order ${orderId}`);
    const order = orders.find(o => o.id === orderId);
    if (!order) throw new Error('Order not found');
    const newTotal = (parseFloat(order.total) - parseFloat(subsidyAmount)).toFixed(2);
    try {
      await supabaseClient.updateOrder(orderId, {
        subsidy_amount: parseFloat(subsidyAmount),
        subsidy_applied_by: appliedBy,
        total: parseFloat(newTotal)
      });
      console.log('✅ Subsidy applied in Supabase');
      await loadOrders();
    } catch (error: any) {
      console.error('❌ Error applying subsidy in Supabase:', error);
      throw new Error('Error al aplicar subsidio: ' + error.message);
    }
  };

  // Filter orders
  const filteredOrders = (searchTerm: string, company: string, status?: string) => {
    return orders.filter(order => {
      const matchesSearch = order.name.toLowerCase().includes(searchTerm.toLowerCase()) || order.orderNo.includes(searchTerm) || order.product.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCompany = company === 'Todas' || order.company === company;
      const matchesStatus = !status || status === 'Todos' || order.status === status;
      return matchesSearch && matchesCompany && matchesStatus;
    });
  };

  // Show error message if Supabase is not configured
  if (error && !isSupabaseConfigured()) {
    return <OrderContext.Provider value={{
      orders: [],
      loading: false,
      refreshOrders: async () => {},
      // Función vacía para evitar crash
      addOrder: async () => {
        throw new Error(error);
      },
      updateOrderStatus: async () => {
        throw new Error(error);
      },
      updateLabelStatus: async () => {
        throw new Error(error);
      },
      cancelOrder: async () => {
        throw new Error(error);
      },
      applySubsidy: async () => {
        throw new Error(error);
      },
      filteredOrders: () => []
    }} data-id="element-678">
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4" data-id="element-679">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-red-200 dark:border-red-800" data-id="element-680">
            <div className="text-center" data-id="element-681">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-682">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-683">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" data-id="element-684" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-685">
                Base de datos no configurada
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-686">{error}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-687">
                Por favor, configura las variables de entorno VITE_SUPABASE_URL
                y VITE_SUPABASE_ANON_KEY en tu servidor de deployment.
              </p>
            </div>
          </div>
        </div>
      </OrderContext.Provider>;
  }
  return <OrderContext.Provider value={{
    orders,
    loading,
    refreshOrders: loadOrders,
    // AQUI SE EXPONE LA FUNCIÓN
    addOrder,
    updateOrderStatus,
    updateLabelStatus,
    cancelOrder,
    applySubsidy,
    filteredOrders
  }} data-id="element-688">
      {children}
    </OrderContext.Provider>;
}
export function useOrders() {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
}